import React, { ReactElement, ReactNode } from 'react';
import { Box, Button, CircularProgress, DialogTitle, Grid2, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Deljeni gradivni blokovi za dijalog-forme, da sve forme izgledaju i ponašaju se isto:
 * naslov sa bojenom ivicom (i opcionim podnaslovom / dugmetom za zatvaranje), naslov sekcije
 * unutar forme, i red sa akcijama (Odustani + primarno dugme sa spinner-om dok se čuva).
 */

export type FormAccent = 'primary' | 'warning' | 'error';

const accentColors: Record<FormAccent, string> = {
    primary: '#1976d2',
    warning: '#ed6c02',
    error: '#d32f2f',
};

type FormDialogTitleProps = {
    children: ReactNode;
    /** Kratko objašnjenje ispod naslova (npr. čemu služi forma). */
    subtitle?: ReactNode;
    /** Ako je zadato, u naslovu se prikazuje X za zatvaranje. */
    onClose?: () => void;
    accent?: FormAccent;
};

export function FormDialogTitle(props: FormDialogTitleProps): ReactElement {
    const { children, subtitle, onClose, accent = 'primary' } = props;

    return (
        <DialogTitle sx={{ displayPrint: 'none', borderLeft: `10px ${accentColors[accent]} solid`, pl: 1, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                    <Typography variant="body1" component="h6" style={{ fontSize: 25 }}>
                        {children}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {onClose && (
                    <IconButton color="inherit" onClick={onClose} aria-label="Zatvori">
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>
        </DialogTitle>
    );
}

type FormSectionProps = {
    title: ReactNode;
    /** Ako je forma Grid2 kontejner, sekcija zauzima ceo red; inače je običan blok. */
    inGrid?: boolean;
};

export function FormSection(props: FormSectionProps): ReactElement {
    const { title, inGrid = true } = props;

    const content = (
        <>
            <Typography variant="subtitle1" sx={{ mt: 1 }}>
                {title}
            </Typography>
            <hr />
        </>
    );

    return inGrid ? <Grid2 size={{ xs: 12 }}>{content}</Grid2> : <Box>{content}</Box>;
}

type FormActionsProps = {
    onSubmit(): void;
    submitLabel: ReactNode;
    /** Bez onCancel prikazuje se samo primarno dugme. */
    onCancel?: () => void;
    cancelLabel?: ReactNode;
    color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning';
    disabled?: boolean;
    /** Dok traje čuvanje: primarno dugme prikazuje spinner, oba su onemogućena. */
    pending?: boolean;
    /** Ako je forma Grid2 kontejner, akcije zauzimaju ceo red; inače su običan blok. */
    inGrid?: boolean;
};

export function FormActions(props: FormActionsProps): ReactElement {
    const {
        onSubmit,
        submitLabel,
        onCancel,
        cancelLabel = 'Odustani',
        color = 'primary',
        disabled = false,
        pending = false,
        inGrid = true,
    } = props;

    const content = (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                mt: 1,
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                justifyContent: 'flex-end',
            }}
        >
            {onCancel && (
                <Button
                    variant="outlined"
                    onClick={onCancel}
                    disabled={pending}
                    sx={{ minWidth: { sm: 160 }, textTransform: 'none' }}
                >
                    {cancelLabel}
                </Button>
            )}
            <Button
                variant="contained"
                color={color}
                onClick={onSubmit}
                disabled={disabled || pending}
                sx={{ minWidth: { sm: 300 }, textTransform: 'none' }}
            >
                {pending ? <CircularProgress size={15} color="inherit" /> : submitLabel}
            </Button>
        </Box>
    );

    return inGrid ? <Grid2 size={{ xs: 12 }}>{content}</Grid2> : content;
}
